import React, { useMemo } from "react";
import { PageHeader, Card, StatusPill } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { useAirtable } from "../hooks/useAirtable";
import { str, fmtDate, indexById, linkedName } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";
import PeriodScopeBar, { usePeriodFilter } from "../components/ui-bj/PeriodScopeBar";
import { CheckCircle, XCircle } from "@phosphor-icons/react";

const Yes = () => <CheckCircle size={16} weight="fill" className="text-[#14281E]" />;
const No = () => <XCircle size={16} weight="fill" className="text-[#5E4D40]" />;

const BusinessFoundationPage = () => {
  const llc = useAirtable("llc");
  const clients = useAirtable("clients");
  const clientMap = useMemo(() => indexById(clients.records), [clients.records]);
  const { period, setPeriod, scope, setScope, filterByDate } = usePeriodFilter("all");

  if (llc.loading || clients.loading) return <Loading label="Loading business foundation records" />;
  if (llc.error) return <ErrorState message={llc.error} onRetry={llc.refetch} />;

  const rows = llc.records
    .map((r) => {
      const f = r.fields || {};
      return {
        id: r.id,
        client: linkedName(f["Client Link"], clientMap),
        businessName: str(f["Business Name"]),
        state: str(f["State Filed"]),
        stage: str(f["Formation Stage"]),
        einStatus: str(f["EIN Status"]),
        einNumber: str(f["EIN Number"]),
        dateApproved: f["Date Approved"],
        operatingAgreement: !!f["Operating Agreement"],
        businessBanking: !!f["Business Banking"],
      };
    })
    .sort((a, b) => (b.dateApproved || "").localeCompare(a.dateApproved || ""));

  // Period filter scopes Business Foundation to "Date Approved".
  const visible = rows.filter((r) => filterByDate(r.dateApproved));

  return (
    <div data-testid="business-foundation-page">
      <PageHeader
        eyebrow="DASHBOARD / BUSINESS FOUNDATION & ENTITY SETUP"
        title="Business Foundation Setup"
        subtitle={`${visible.length} of ${rows.length} entities • ${scope === "period" ? "filtered by Date Approved" : "all-time view"} • Sorted by approval date (newest first).`}
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodScopeBar scope={scope} setScope={setScope} period={period} setPeriod={setPeriod} testidPrefix="foundation-scope" />
            <ExportButtons
              title="Business Foundation Setup"
              filename="business-foundation-and-entity-setup"
              testidPrefix="export-foundation"
              columns={[
                { key: "client", label: "Client" },
                { key: "businessName", label: "Business Name" },
                { key: "state", label: "State" },
                { key: "stage", label: "Formation Stage" },
                { key: "einStatus", label: "EIN Status" },
                { key: "einNumber", label: "EIN" },
                { key: "dateApproved", label: "Date Approved" },
                { key: "operatingAgreement", label: "Operating Agreement", get: (r) => (r.operatingAgreement ? "Yes" : "No") },
                { key: "businessBanking", label: "Business Banking", get: (r) => (r.businessBanking ? "Yes" : "No") },
              ]}
              rows={visible}
            />
          </div>
        }
      />

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[1100px]">
          <thead>
            <tr className="text-left">
              {["Client", "Business Name", "State", "Stage", "EIN Status", "EIN", "Approved", "Op. Agreement", "Banking"].map((h) => (
                <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={9} className="py-10 text-center text-[#5E4D40] text-sm">No entities for this filter.</td></tr>
            )}
            {visible.map((r, i) => (
              <tr key={r.id} data-testid={`foundation-row-${i}`} className="bj-table-row">
                <td className="py-3 pr-3 text-[#2A1C15] text-sm">{r.client}</td>
                <td className="py-3 pr-3 text-[#6E5022] text-sm">{r.businessName}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm">{r.state}</td>
                <td className="py-3 pr-3">{r.stage !== "—" ? <StatusPill status={r.stage} /> : <span className="text-[#5E4D40] text-sm">—</span>}</td>
                <td className="py-3 pr-3">{r.einStatus !== "—" ? <StatusPill status={r.einStatus} /> : <span className="text-[#5E4D40] text-sm">—</span>}</td>
                <td className="py-3 pr-3 text-[#2A1C15] text-sm bj-tnum">{r.einNumber}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm bj-tnum">{fmtDate(r.dateApproved)}</td>
                <td className="py-3 pr-3">{r.operatingAgreement ? <Yes /> : <No />}</td>
                <td className="py-3 pr-3">{r.businessBanking ? <Yes /> : <No />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default BusinessFoundationPage;
